import {
  parseDate,
  type DateValue,
  type CalendarDate,
  type DayOfWeek,
} from '@internationalized/date';
import React, { type ReactNode, useCallback, useRef, useMemo } from 'react';
import {
  DatePicker as DatePickerRAC,
  Group as GroupRAC,
  DateInput as DateInputRAC,
  DateSegment as DateSegmentRAC,
  Button as ButtonRAC,
  Popover as PopoverRAC,
  Dialog as DialogRAC,
  Calendar as CalendarRAC,
  CalendarGrid as CalendarGridRAC,
  CalendarCell as CalendarCellRAC,
  Heading as HeadingRAC,
} from 'react-aria-components';

import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { getFirstDayOfTheWeek } from '../../date-localization/get-first-day-of-the-week';
import {
  DEFAULT_AVAILABILITY,
  isDateAvailable,
} from '../../field-settings/settings-shared/availability/availability';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { type DatePickerFieldProps } from './date-picker-field-props';

export type DatePickerProps = {
  children: ReactNode;
};

export const DatePicker = ({ children }: DatePickerProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    FieldLayout,
    hasError,
    readOnly,
  } = useFieldPropsV2<DatePickerFieldProps>();

  const handleChange = useCallback(
    (dateValue: DateValue | null) => {
      const stringValue = dateValueToString(dateValue);
      onChange(stringValue);
    },
    [onChange],
  );

  const labelElement = findChildOfType(children, DatePicker.Label);
  const controlElement = findChildOfType(children, DatePicker.Control);
  const calendarElement = findChildOfType(children, DatePicker.Calendar);
  const descriptionElement = findChildOfType(children, DatePicker.Description);
  const errorElement = findChildOfType(children, DatePicker.Error);

  const dateValue = stringToDateValue(value);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <DatePickerRAC
          value={dateValue}
          onChange={handleChange}
          isDisabled={disabled}
          isInvalid={hasError}
          isReadOnly={readOnly}
          isRequired={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {controlElement}
          {calendarElement}
        </DatePickerRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

export type ControlProps = {
  children: ReactNode;
  className?: string;
};

const Control = ({ children, className }: ControlProps) => {
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { hasError } = useFieldPropsV2<DatePickerFieldProps>();

  return (
    <GroupRAC
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      data-invalid={hasError}
    >
      {children}
    </GroupRAC>
  );
};

export type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { id } = useFieldPropsV2<DatePickerFieldProps>();

  const firstSegmentRef = useRef<HTMLElement | null>(null);
  const hasAssignedRef = useRef(false);

  useFocusFieldEvent(() => {
    firstSegmentRef.current?.focus();
  }, id);

  return (
    <DateInputRAC className={className}>
      {(segment) => (
        <DateSegmentRAC
          segment={segment}
          ref={(node) => {
            if (!node && hasAssignedRef.current) {
              firstSegmentRef.current = null;
              hasAssignedRef.current = false;
              return;
            }

            if (node && !hasAssignedRef.current) {
              firstSegmentRef.current = node;
              hasAssignedRef.current = true;
              return;
            }
          }}
        />
      )}
    </DateInputRAC>
  );
};

export type TriggerProps = {
  className?: string;
};

const Trigger = ({ className }: TriggerProps) => {
  return <ButtonRAC className={className} />;
};

export type CalendarProps = {
  children: ReactNode;
  className?: string;
};

const Calendar = ({ children, className }: CalendarProps) => {
  const { regionalFormat } = useConfig();
  const { availability } = useFieldPropsV2<DatePickerFieldProps>();

  const firstDayOfWeek = useMemo(
    () => getFirstDayOfTheWeek(regionalFormat),
    [regionalFormat],
  );

  const isDateUnavailable = useMemo(() => {
    return (date: DateValue) => {
      const jsDate = date.toDate('UTC');
      return !isDateAvailable(jsDate, availability || DEFAULT_AVAILABILITY);
    };
  }, [availability]);

  const dayElement = findChildOfType(children, Calendar.Day) ?? (
    <Calendar.Day />
  );

  return (
    <PopoverRAC className={className}>
      <DialogRAC>
        <CalendarRAC
          firstDayOfWeek={convertFirstDayOfWeek(firstDayOfWeek)}
          isDateUnavailable={isDateUnavailable}
        >
          <header>
            <ButtonRAC slot="previous">‹</ButtonRAC>
            <HeadingRAC />
            <ButtonRAC slot="next">›</ButtonRAC>
          </header>
          <CalendarGridRAC>
            {(date) =>
              applyInternalProps<DayInternalProps>(dayElement, {
                date,
              })!
            }
          </CalendarGridRAC>
        </CalendarRAC>
      </DialogRAC>
    </PopoverRAC>
  );
};

export type DayProps = {
  className?: string;
};

type DayInternalProps = {
  date: CalendarDate;
};

const Day = ({ className, ...internal }: DayProps) => {
  const { date } = internal as DayInternalProps;
  return <CalendarCellRAC className={className} date={date} />;
};

Calendar.Day = Day;

DatePicker.Label = Label;
DatePicker.Control = Control;
DatePicker.Input = Input;
DatePicker.Trigger = Trigger;
DatePicker.Calendar = Calendar;
DatePicker.Description = Description;
DatePicker.Error = Error;

function stringToDateValue(value: string | null | undefined) {
  if (!value) {
    return null;
  }
  try {
    return parseDate(value);
  } catch {
    return null;
  }
}

function dateValueToString(date: DateValue | null) {
  if (!date) {
    return null;
  }
  const year = String(date.year).padStart(4, '0');
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function convertFirstDayOfWeek(firstDayOfWeek: number): DayOfWeek {
  // getFirstDayOfTheWeek returns 0-6, where 0 = Sunday
  // React Aria expects 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'
  const days: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  return days[firstDayOfWeek] || 'mon';
}
